import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';

// Chart palette pulled from index.css tokens (dark-mode aware).
const ORDERS_COLOR = 'var(--chart-2)';

function ChartTooltip({ active, payload, label }: {
    active?: boolean;
    payload?: Array<{ value: number; name: string; color?: string }>;
    label?: string;
}) {
    if (!active || !payload?.length) return null;
    return (
        <div className="min-w-[190px] rounded-xl border border-border bg-popover p-3 shadow-lg">
            <p className="mb-2 text-xs font-semibold text-muted-foreground">{label}</p>
            <div className="space-y-1.5">
                {payload.map((entry) => (
                    <div key={entry.name} className="flex items-center justify-between gap-6 text-sm">
                        <span className="flex items-center gap-2 text-muted-foreground">
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
                            Orders
                        </span>
                        <span className="font-semibold text-foreground tabular-nums">
                            {entry.value}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function LegendDot({ color, label }: { color: string; label: string }) {
    return (
        <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
            {label}
        </div>
    );
}

interface OrderChartProps {
    salesChartData: { date: string; orders: number }[];
    days: number;
}

export function OrderChart({ salesChartData, days }: OrderChartProps) {
    return (
        <Card className="lg:col-span-3 gap-0 rounded-xl border-border/70 py-0 shadow-xs">
            <CardHeader className="flex! flex-col items-start justify-between gap-3 px-5 pt-5 pb-2 sm:flex-row sm:items-center">
                <div className="space-y-1">
                    <CardTitle className="text-base font-semibold">
                        Order Performance
                    </CardTitle>
                    <CardDescription className="text-xs">
                        {`Orders across the last ${days} days.`}
                    </CardDescription>
                </div>
                <div className="hidden items-center gap-4 text-xs text-muted-foreground sm:flex">
                    <LegendDot color={ORDERS_COLOR} label="Orders" />
                </div>
            </CardHeader>
            <CardContent className="px-3 pt-4 pb-4">
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                        data={salesChartData}
                        barGap={4}
                        margin={{
                            top: 8, right: 8, left: 0, bottom: 0,
                        }}
                    >
                        <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="var(--border)" />
                        <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }} dy={8} />
                        <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
                            width={40}
                            allowDecimals={false}
                        />
                        <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--muted)' }} />
                        <Bar dataKey="orders" name="Orders" fill={ORDERS_COLOR} radius={[4, 4, 0, 0]} maxBarSize={26} />
                    </BarChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
}
